
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { getEnv } from "./runtimeConfig";
import { useAuthStore } from "@/lib/store/useAuthStore";


const api = axios.create({
    // baseURL será injetada via interceptor para suportar runtime dinâmico
});

let chat_api = axios.create({
    // baseURL será injetada via interceptor para suportar runtime dinâmico
});

let notification_api = axios.create({
    // baseURL será injetada via interceptor para suportar runtime dinâmico
});

/**
 * Tipos de API disponíveis
 */
export type ApiType = 'api' | 'chat_api' | 'notification_api';

/**
 * Mapa de instâncias de API
 */
const apiInstances: Record<ApiType, AxiosInstance> = {
    api,
    chat_api,
    notification_api,
};

/**
 * Retorna a instância da API baseada no tipo
 */
export const getApiInstance = (type: ApiType = 'api'): AxiosInstance => {
    return apiInstances[type];
};

/**
 * Interface para os dados de autenticação do Squad
 */
interface SquadAuthData {
    access_token?: string;
    domain?: string;
    baseUrl?: string;
    email?: string;
    user_name?: string;
    expires_in?: number;
    user_id?: string;
}

/**
 * Obtém os dados de autenticação do Squad a partir do Zustand
 */
const getSquadAuthFromZustand = (): SquadAuthData | null => {
    return useAuthStore.getState().squadToken ?? null;
};

/**
 * Obtém o token de acesso do Zustand
 */
const getAccessToken = (): string | null => {
    return useAuthStore.getState().jwtTokens?.accessToken ?? null;
};

/**
 * Obtém o domínio do usuário a partir do Squad Auth (Zustand)
 */
const getUserDomain = (): string => {
    const squadAuth = getSquadAuthFromZustand();
    
    if (squadAuth?.domain) {
        return squadAuth.domain;
    }
    
    if (squadAuth?.email) {
        return squadAuth.email.split('@')[1];
    }
    
    return 'digivox.com.br';
};

/**
 * Atualiza a instância chat_api com a baseUrl do Squad Auth
 */
export const updateChatApiFromSquadAuth = (): void => {
    const squadAuth = getSquadAuthFromZustand();
    
    if (squadAuth?.baseUrl) {
        chat_api = axios.create({
            baseURL: squadAuth.baseUrl.endsWith('/') 
                ? `${squadAuth.baseUrl}api/v1/` 
                : `${squadAuth.baseUrl}/api/v1/`,
            timeout: 60000,
            headers: {
                'Content-Type': 'application/json',
                'Customer': getUserDomain(),
            },
        });
        
        chat_api.interceptors.request.use(chatApiRequestInterceptor);
        chat_api.interceptors.response.use(response => response, errorResponseInterceptor);
        
        apiInstances.chat_api = chat_api;
    }
};

/**
 * Interceptor de requisição para a API principal
 * Usa o token do Zustand e injeta a baseURL dinâmica
 */
const authRequestInterceptor = (config: InternalAxiosRequestConfig) => {
    // Injeta baseURL dinâmica se não estiver definida
    if (!config.baseURL) {
        const dynamicBaseURL = getEnv("NEXT_PUBLIC_API_BASE_URL") || "https://omnichannel-dev.smartspace.us";
        config.baseURL = `${dynamicBaseURL}/api-gateway/v1/`;
    }

    const token = getAccessToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
};

/**
 * Interceptor de requisição para a Chat API
 * Usa o access_token do localStorage AUTHORIZATION_SQUAD e injeta a baseURL dinâmica
 */
const chatApiRequestInterceptor = (config: InternalAxiosRequestConfig) => {
    // Injeta baseURL dinâmica se não estiver definida
    if (!config.baseURL) {
        const dynamicChatURL = getEnv("NEXT_PUBLIC_CHAT_API_BASE_URL") || "https://api-labchat.digivox.com.br";
        config.baseURL = `${dynamicChatURL}/api/v1/`;
    }

    const squadAuth = getSquadAuthFromZustand();
    
    if (squadAuth?.access_token) {
        config.headers.Authorization = `Bearer ${squadAuth.access_token}`;
    }
    
    config.headers['Customer'] = getUserDomain();
    
    return config;
};

/**
 * Interceptor de resposta para tratamento de erros
 */
const errorResponseInterceptor = (error: AxiosError) => {
    if (error.response?.status === 401) {
        console.warn('Unauthorized - Error 401');
    }
    return Promise.reject(error);
};


/**
 * Interceptor de requisição para a Notification API
 */
const notificationApiRequestInterceptor = (config: InternalAxiosRequestConfig) => {
    if (!config.baseURL) {
        const dynamicNotificationURL = getEnv("NEXT_PUBLIC_NOTIFICATION_BASE_URL") || "https://notification-dev.smartspace.us";
        config.baseURL = `${dynamicNotificationURL}/api/`;
    }

    const token = getEnv("NEXT_PUBLIC_NOTIFICATION_TOKEN");
    if (token) {
        config.headers['X-Auth-Token'] = token;
    }
    
    return config;
};

api.interceptors.request.use(authRequestInterceptor);
api.interceptors.response.use(response => response, errorResponseInterceptor);

chat_api.interceptors.request.use(chatApiRequestInterceptor);
chat_api.interceptors.response.use(response => response, errorResponseInterceptor);

notification_api.interceptors.request.use(notificationApiRequestInterceptor);
notification_api.interceptors.response.use(response => response, errorResponseInterceptor);

export { chat_api, notification_api };
export default api;